/**
 * Llamadas del backoffice (epica ADM).
 */
import type {
  ActualizarContenidoProductoDto,
  ActualizarPaqueteProductoDto,
  ActualizarSeoDto,
  EdicionMasivaProductosDto,
  EstadoIntegracionRandom,
  FiltrosPedidosDto,
  FiltrosProductosAdminDto,
  ImagenProducto,
  ListosParaPublicar,
  OpcionesFiltroProductos,
  Paginado,
  PanelAdministracion,
  Pedido,
  PedidoAdminDetalle,
  ProductoAdminDetalle,
  ProductoAdminResumen,
  RespuestaEdicionMasiva,
  RespuestaEliminacionNoInformados,
  SincronizacionRandom,
  TerminoSinResultado,
} from '@elcarpintero/compartido';

import { pedir, pedirArchivo } from './cliente-api';

export function obtenerPanel(senal?: AbortSignal): Promise<PanelAdministracion> {
  return pedir<PanelAdministracion>('/admin/panel', { ...(senal ? { senal } : {}) });
}

export function obtenerProductos(
  filtros: FiltrosProductosAdminDto,
  senal?: AbortSignal,
): Promise<Paginado<ProductoAdminResumen>> {
  // Los nombres de la query son los del backend (`consultarProductosAdmin`):
  // en singular las listas, porque cada valor viaja como un parametro repetido.
  return pedir<Paginado<ProductoAdminResumen>>('/admin/productos', {
    parametros: {
      pagina: filtros.pagina,
      porPagina: filtros.porPagina,
      termino: filtros.termino,
      linea: filtros.lineas,
      categoria: filtros.categorias,
      marca: filtros.marcas,
      visibilidad: filtros.visibilidad,
      conFoto: filtros.conFoto,
      conPrecio: filtros.conPrecio,
      stockMin: filtros.stockMin,
      stockMax: filtros.stockMax,
      soloDestacados: filtros.soloDestacados,
      orden: filtros.orden,
    },
    ...(senal ? { senal } : {}),
  });
}

/** Lineas, categorias y marcas que ofrecen los filtros de la tabla (ADM-02). */
export function obtenerOpcionesFiltroProductos(senal?: AbortSignal): Promise<OpcionesFiltroProductos> {
  return pedir<OpcionesFiltroProductos>('/admin/productos/opciones-filtro', {
    ...(senal ? { senal } : {}),
  });
}

/** Detalle completo de un producto para su pantalla propia (ADM-02). */
export function obtenerProductoAdmin(id: number, senal?: AbortSignal): Promise<ProductoAdminDetalle> {
  return pedir<ProductoAdminDetalle>(`/admin/productos/${id}`, { ...(senal ? { senal } : {}) });
}

/** Publicar/ocultar un producto desde su pantalla de detalle (ADM-02). */
export function actualizarVisibilidadProducto(id: number, visible: boolean): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/visible`, { metodo: 'PATCH', cuerpo: { visible } });
}

/** Descripcion y ficha tecnica: el contenido propio del sitio (ADM-02). */
export function actualizarContenidoProducto(
  id: number,
  datos: ActualizarContenidoProductoDto,
): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/contenido`, { metodo: 'PATCH', cuerpo: datos });
}

/**
 * Cuantas unidades de Random forman una unidad de la tienda (VTA-01). Responde
 * el producto con precio y stock ya recalculados.
 */
export function actualizarPaqueteProducto(
  id: number,
  datos: ActualizarPaqueteProductoDto,
): Promise<ProductoAdminDetalle> {
  return pedir<ProductoAdminDetalle>(`/admin/productos/${id}/paquete`, {
    metodo: 'PATCH',
    cuerpo: datos,
  });
}

/**
 * Publica u oculta varios productos a la vez (ADM-03). Precio, stock y
 * categoria no viajan aca: son de Random.
 */
export function actualizarProductosMasivo(
  datos: EdicionMasivaProductosDto,
): Promise<RespuestaEdicionMasiva> {
  return pedir<RespuestaEdicionMasiva>('/admin/productos/masivo', { metodo: 'PATCH', cuerpo: datos });
}

/** Cuantos ocultos tienen foto, precio y stock y se pueden publicar de una (ADM-03). */
export function obtenerListosParaPublicar(senal?: AbortSignal): Promise<ListosParaPublicar> {
  return pedir<ListosParaPublicar>('/admin/productos/listos-para-publicar', {
    ...(senal ? { senal } : {}),
  });
}

/** Publica todos los que estan listos; la condicion la decide el servidor (ADM-03). */
export function publicarListos(): Promise<RespuestaEdicionMasiva> {
  return pedir<RespuestaEdicionMasiva>('/admin/productos/publicar-listos', { metodo: 'POST' });
}

/**
 * Sube una foto al producto (ADM-04).
 *
 * El archivo viaja como bytes crudos con su propio tipo, sin `multipart` y sin
 * envolverlo en un JSON —que lo haria un tercio mas pesado al pasarlo a
 * base64—. Es el mismo camino que ya usa la busqueda por foto.
 *
 * El navegador NO mide ni achica nada antes de mandar: el archivo que se
 * publica no es el que se eligio (el servidor lo endereza, lo achica y lo
 * convierte a WebP), asi que medirlo aca daria las medidas de algo que nadie va
 * a descargar. Las que valen vienen en la respuesta.
 */
export function subirFotoProducto(id: number, archivo: File): Promise<ImagenProducto> {
  return pedir<ImagenProducto>(`/admin/productos/${id}/fotos`, {
    metodo: 'POST',
    cuerpoBinario: archivo,
  });
}

/** Quita una foto del producto, del catalogo y del almacen (ADM-04). */
export function eliminarFotoProducto(id: number, imagenId: number): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/fotos/${imagenId}`, { metodo: 'DELETE' });
}

/**
 * Texto alternativo de una foto (ADM-04). Vacio se guarda como `null`: sin
 * texto propio, la ficha usa el nombre del producto.
 */
export function describirFotoProducto(
  id: number,
  imagenId: number,
  textoAlternativo: string | null,
): Promise<ImagenProducto> {
  return pedir<ImagenProducto>(`/admin/productos/${id}/fotos/${imagenId}`, {
    metodo: 'PATCH',
    cuerpo: { textoAlternativo },
  });
}

/**
 * Sube o baja una foto un lugar. Devuelve la galeria completa: mover cambia dos
 * fotos, no una. La primera es la principal.
 */
export function moverFotoProducto(
  id: number,
  imagenId: number,
  direccion: 'subir' | 'bajar',
): Promise<ImagenProducto[]> {
  return pedir<ImagenProducto[]>(`/admin/productos/${id}/fotos/${imagenId}/mover`, {
    metodo: 'POST',
    cuerpo: { direccion },
  });
}

/** Eliminar un producto (ADM-02). No tiene vuelta atras. */
export function eliminarProducto(id: number): Promise<void> {
  return pedir<void>(`/admin/productos/${id}`, { metodo: 'DELETE' });
}

export function obtenerPedidos(
  filtros: FiltrosPedidosDto,
  senal?: AbortSignal,
): Promise<Paginado<Pedido>> {
  // Los nombres de la query son los del backend (`consultarPedidos`).
  return pedir<Paginado<Pedido>>('/admin/pedidos', {
    parametros: {
      pagina: filtros.pagina,
      porPagina: filtros.porPagina,
      termino: filtros.termino,
      estado: filtros.estado,
      random: filtros.random,
      tipoEntrega: filtros.tipoEntrega,
      tipoDocumento: filtros.tipoDocumento,
      desde: filtros.desde,
      hasta: filtros.hasta,
      totalMin: filtros.totalMin,
      totalMax: filtros.totalMax,
      orden: filtros.orden,
    },
    ...(senal ? { senal } : {}),
  });
}

/** Todo lo que se sabe de un pedido: el modal del historial (ADM-09). */
export function obtenerPedidoAdmin(id: number, senal?: AbortSignal): Promise<PedidoAdminDetalle> {
  return pedir<PedidoAdminDetalle>(`/admin/pedidos/${id}`, senal ? { senal } : {});
}

/** El pedido en PDF para imprimir, con logo y fotos (ADM-09). */
export function descargarPedidoPdf(id: number): Promise<Blob> {
  return pedirArchivo(`/admin/pedidos/${id}/pdf`);
}

/** Titulo y descripcion SEO de un producto (ADM-08). */
export function actualizarSeoProducto(id: number, datos: ActualizarSeoDto): Promise<void> {
  return pedir<void>(`/admin/productos/${id}/seo`, { metodo: 'PATCH', cuerpo: datos });
}

export function obtenerBusquedasSinResultado(
  opciones: { pagina?: number; senal?: AbortSignal } = {},
): Promise<Paginado<TerminoSinResultado>> {
  return pedir<Paginado<TerminoSinResultado>>('/admin/busquedas', {
    parametros: { pagina: opciones.pagina },
    ...(opciones.senal ? { senal: opciones.senal } : {}),
  });
}

export function obtenerIntegracionRandom(senal?: AbortSignal): Promise<EstadoIntegracionRandom> {
  return pedir<EstadoIntegracionRandom>('/admin/random', { ...(senal ? { senal } : {}) });
}

/**
 * Boton de "sincronizar ahora" (RND-01). Devuelve la corrida, haya funcionado o
 * no: mientras Random siga apagado, lo que llega es la corrida que lo dice.
 */
export function sincronizarConRandom(): Promise<SincronizacionRandom> {
  return pedir<SincronizacionRandom>('/admin/random/sincronizar', { metodo: 'POST' });
}

/**
 * Acepta borrar los productos que Random dejo de informar y la sincronizacion
 * no borro sola por ser demasiados (RND-01). Sin cuerpo: se borra lo que este
 * marcado en el servidor en ese momento.
 */
export function eliminarProductosNoInformados(): Promise<RespuestaEliminacionNoInformados> {
  return pedir<RespuestaEliminacionNoInformados>('/admin/random/eliminar-no-informados', {
    metodo: 'POST',
  });
}
